'use client';

import { Badge } from '@/components/ui/badge';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { CommandLoading, Command as CommandPrimitive } from 'cmdk';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { InputProps } from '../ui/input';
import { Skeleton } from '../ui/skeleton';

export type ComboboxMultipleOption<T = any> = {
    label: string | React.ReactNode;
    value: string;
    disabled?: boolean;
    _origin?: T;
};
export type ComboboxMultipleProps = {
    options?: ComboboxMultipleOption[];
    /**values selected */
    value?: ComboboxMultipleOption[];
    /**onChange values */
    onChange?: (values: ComboboxMultipleOption[]) => void;

    /**pass props for input search */
    inputProps?: InputProps;

    /**
     * status when fetching API
     */
    isSearching?: boolean;
    /**
     * Hàm search debounce đẩy keyword search ra ngoài sử dụng cho useQuery
     *
     * Debounce mặc định là 1000ms
     */
    onSearch?: (keyword: string) => void | Promise<void>;
    /**delay time ms */
    delay?: number;

    wrapperClassName?: string;

    //------------------ custom render
    renderOption?: (option: any) => React.ReactNode;
    disabled?: boolean;
};

export const ComboboxMultiple = React.memo((props: ComboboxMultipleProps) => {
    const {
        options = [],
        inputProps,
        onChange: setSelected,
        value: selected = [],
        isSearching = false,
        delay = 1000,
        onSearch,
        renderOption,
        disabled,
        wrapperClassName,
    } = props;

    const transDef = useTranslations('System');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const debounceRef = React.useRef<NodeJS.Timeout>();
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);

    /**handle click button X */
    const handleUnselect = React.useCallback(
        (unSelect: ComboboxMultipleOption) => {
            setSelected?.(selected.filter((s) => s.value !== unSelect.value));
        },
        [selected, setSelected]
    );

    /**handle delete with key Delete or Backspace */
    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const input = inputRef.current;
            if (input) {
                if (e.key === 'Delete' || e.key === 'Backspace') {
                    if (input.value === '') {
                        const newSelected = [...selected];
                        newSelected.pop();
                        setSelected?.(newSelected);
                    }
                }
                // This is not a default behaviour of the <input /> field
                if (e.key === 'Escape') {
                    input.blur();
                }
            }
        },
        [selected, setSelected]
    );

    /**custom filter cmdk */
    const handleFilter = React.useCallback(
        (value: string, search: string) => {
            const valToLower = value?.toLowerCase();
            const searchToLower = search?.toLowerCase();

            const label = options.find((x) => x.value === valToLower)?.label;
            if (
                valToLower.includes(searchToLower) ||
                (typeof label === 'string' &&
                    label.toLowerCase().includes(searchToLower))
            ) {
                return 1;
            }
            return 0;
        },
        [options]
    );

    /**handle callback search API */
    const handleSearch = React.useCallback(
        (keyword: string) => {
            setInputValue(keyword);
            // handle callback search API when have onSearch
            if (typeof onSearch === 'function') {
                setIsLoading(true);
                clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => {
                    onSearch(keyword);
                    setIsLoading(false);
                }, delay);
            }
        },
        [delay, onSearch]
    );

    const selectables = React.useMemo(() => {
        const objSelected: Record<string, boolean> = {};
        selected.forEach((x) => {
            if (x) {
                objSelected[x.value] = true;
            }
        });

        return options.filter((opt) => !objSelected[opt.value]);
    }, [options, selected]);

    const isApiHandler = isLoading || isSearching;

    return (
        <Command
            onKeyDown={handleKeyDown}
            shouldFilter={typeof onSearch === 'function' ? false : true}
            filter={handleFilter}
            className='h-auto overflow-visible bg-background'
        >
            <div
                className={cn(
                    'group min-h-10 rounded-md border border-input px-2 py-1 text-sm',
                    wrapperClassName,
                    {
                        'pl-3': selected.length > 0,
                    }
                )}
            >
                <div className='flex flex-wrap items-center gap-1'>
                    {selected.map((opt) => {
                        return (
                            <Badge
                                key={opt?.value}
                                variant='default'
                                className='h-7 data-[disabled="true"]:cursor-not-allowed data-[disabled="true"]:bg-primary/80'
                                data-disabled={disabled}
                            >
                                {opt?.label}
                                <button
                                    disabled={disabled}
                                    className='ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleUnselect(opt);
                                        }
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onClick={() => handleUnselect(opt)}
                                >
                                    <X className='text-muted-foregroun ml-2 h-3 w-3 hover:text-foreground' />
                                </button>
                            </Badge>
                        );
                    })}
                    {/* Avoid having the "Search" Icon */}
                    <CommandPrimitive.Input
                        placeholder={transDef('System_Search') + ' ...'}
                        className={cn(
                            'h-8 flex-1 rounded-md border-none bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50',
                            {
                                'ml-1': selected.length > 0,
                            }
                        )}
                        // only overwrite above props
                        {...(inputProps ?? {})}
                        ref={inputRef}
                        value={inputValue}
                        onValueChange={handleSearch}
                        onBlur={() => setOpen(false)}
                        onFocus={() => setOpen(true)}
                        autoComplete='off'
                        disabled={disabled}
                    />
                </div>
            </div>
            <div className='relative mt-1'>
                <CommandList>
                    {open && (
                        <div className='absolute top-0 z-20 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in'>
                            {isApiHandler ? (
                                <CommandLoading className='p-2'>
                                    <Skeleton className='h-6 w-11/12' />
                                    <Skeleton className='mt-2 h-6 w-9/12' />
                                </CommandLoading>
                            ) : selectables.length > 0 ? (
                                <CommandGroup className='h-full overflow-auto'>
                                    {selectables.map((opt) => {
                                        return (
                                            <CommandItem
                                                key={opt.value}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                onSelect={() => {
                                                    setInputValue('');
                                                    setSelected?.([
                                                        ...selected,
                                                        opt,
                                                    ]);
                                                }}
                                                disabled={opt.disabled}
                                                className={'cursor-pointer'}
                                            >
                                                {typeof renderOption ===
                                                'function'
                                                    ? renderOption(
                                                          opt._origin ?? opt
                                                      )
                                                    : opt.label}
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            ) : onSearch ? (
                                <CommandLoading className='py-6 text-center text-sm font-medium'>
                                    {inputValue
                                        ? transDef('System_NoDataFound')
                                        : transDef('System_EnterToSearch')}{' '}
                                    ...
                                </CommandLoading>
                            ) : (
                                <>
                                    {/* show empty for command haven't prop onSearch */}
                                    {inputValue ? (
                                        <CommandEmpty>
                                            {transDef('System_NoDataFound')}
                                        </CommandEmpty>
                                    ) : (
                                        <CommandEmpty>
                                            {transDef('System_NoData')}
                                        </CommandEmpty>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </CommandList>
            </div>
        </Command>
    );
});

ComboboxMultiple.displayName = 'ComboboxMultiple';
